import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { buildIstScheduledAt, todayDateOnly } from '../../core/datetime/ist';
import { OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';

@Component({
  selector: 'app-scheduling',
  standalone: true,
  imports: [DatePipe, FormsModule],
  templateUrl: './scheduling.component.html',
})
export class SchedulingComponent {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  private readonly route = inject(ActivatedRoute);
  readonly hearings = this.ops.hearingList;
  readonly roster = this.ops.rosterList;
  readonly message = signal<string | null>(null);
  readonly canMutate = computed(() => this.rbac.canModule('scheduling'));
  /** Cases DM flagged for hearing (status awaiting_hearing). */
  readonly pendingHearings = computed(() => this.ops.pendingHearingQueue());

  caseNo: string | number = '';
  subject = '';
  venue = 'Court Room 2';
  officer: string | number = '';
  hearingDate = todayDateOnly();
  hearingTime = '11:00';
  readonly minHearingDate = todayDateOnly();
  /** Scheduled hearing selected from the table (for Reschedule / Cancel). */
  readonly selectedHearingId = signal<string | number | null>(null);

  readonly availableOfficers = computed(() =>
    this.roster().filter((o) => o.availability === 'Available' || o.availability === 'In Hearing'),
  );

  readonly selectedHearing = computed(() => {
    const id = this.selectedHearingId();
    if (id == null) return null;
    return this.hearings().find((h) => String(h.id) === String(id)) ?? null;
  });

  constructor() {
    this.caseNo = this.route.snapshot.queryParamMap.get('case') || '';
    effect(() => {
      const pending = this.pendingHearings();
      if (!pending.length) {
        this.caseNo = '';
        this.subject = '';
        return;
      }
      const stillValid = pending.some((p) => String(p.id) === String(this.caseNo));
      if (!stillValid) {
        this.pickPending(pending[0].id, pending[0].subject);
      }
    });
  }

  pickPending(id: string | number, subject: string): void {
    this.caseNo = id;
    this.subject = subject;
    this.selectedHearingId.set(null);
  }

  onCaseChange(id: string | number | string): void {
    const picked = this.pendingHearings().find((p) => String(p.id) === String(id));
    if (picked) {
      this.pickPending(picked.id, picked.subject);
    } else {
      this.caseNo = '';
      this.subject = '';
    }
  }

  selectHearing(h: {
    id: string | number;
    caseNo: string | number;
    subject: string;
    venue?: string;
    scheduledAt?: string;
  }): void {
    this.selectedHearingId.set(h.id);
    this.caseNo = '';
    this.subject = h.subject;
    if (h.venue) this.venue = h.venue;
    if (h.scheduledAt) {
      this.hearingDate = h.scheduledAt.slice(0, 10);
      this.hearingTime = h.scheduledAt.slice(11, 16);
    }
  }

  private scheduledAtForSelection(): string {
    const [year, month, day] = this.hearingDate.split('-').map(Number);
    return buildIstScheduledAt(year, month, day, this.hearingTime);
  }

  private hasValidDateTime(): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(this.hearingDate)
      && /^\d{2}:\d{2}$/.test(this.hearingTime)
      && this.hearingDate >= this.minHearingDate;
  }

  async schedule(): Promise<void> {
    if (!this.canMutate()) {
      this.message.set('Your role cannot schedule hearings.');
      return;
    }
    if (!this.caseNo) {
      this.message.set('Select a case the DM sent for hearing (Case No).');
      return;
    }
    if (!this.hasValidDateTime()) {
      this.message.set('Select a valid hearing date (today or later) and time.');
      return;
    }
    try {
      const alreadyScheduled = this.pendingHearings().some(
        (p) => String(p.id) === String(this.caseNo) && p.status === 'scheduled',
      );
      this.message.set(
        await this.ops.scheduleHearing({
          caseNo: this.caseNo,
          subject: this.subject,
          scheduledAt: this.scheduledAtForSelection(),
          venue: this.venue,
          officers: this.officer,
        }),
      );
      if (alreadyScheduled) {
        this.message.set('Additional hearing sitting scheduled for this case.');
      }
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Schedule failed');
    }
  }

  async reschedule(): Promise<void> {
    if (!this.canMutate()) {
      this.message.set('Your role cannot reschedule hearings.');
      return;
    }
    const hearing = this.selectedHearing();
    if (!hearing) {
      this.message.set('Select a scheduled hearing from the table below, then click Reschedule.');
      return;
    }
    if (!this.hasValidDateTime()) {
      this.message.set('Select a valid hearing date (today or later) and time.');
      return;
    }
    try {
      this.message.set(await this.ops.rescheduleHearing(hearing.id, this.scheduledAtForSelection()));
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Reschedule failed');
    }
  }

  async cancel(): Promise<void> {
    if (!this.canMutate()) {
      this.message.set('Your role cannot cancel hearings.');
      return;
    }
    const hearing = this.selectedHearing();
    if (!hearing) {
      this.message.set('Select a scheduled hearing from the table below, then click Cancel.');
      return;
    }
    try {
      this.message.set(await this.ops.cancelHearing(hearing.id));
      this.selectedHearingId.set(null);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Cancel failed');
    }
  }
}
